// Shared DoD vocabulary — the pure half of the Recursive-Done contract
// (docs/agents/RECURSIVE-DONE-PRD.md §6.5, RATCHET-UNIFICATION-PRD §3).
//
// WHY THIS MODULE EXISTS. Two facts about a task's DoD are read by BOTH the
// plain-Node MCP (which owns the `done` gate) and the Next shell (which now
// renders the DoD in the object peek, #44086 part 2):
//
//   • WHICH CRITERIA GATE `done` — `isBindingCriterion`. A peek that drew the
//     line anywhere else would tell the user a proposal is binding, or that a
//     binding criterion is optional, and be *confidently* wrong.
//   • THE WORK-CLASS VOCABULARY — `WORK_CLASSES`. The correct-a-misclassification
//     control has to offer the same names the classifier can produce; a
//     second hand-maintained list is a fork that goes stale silently, because
//     nothing fails when it does.
//
// So both live here, in a leaf module with NO DB and NO fs, and the MCP
// re-exports them from their historical homes (`done-router.ts`,
// `dod-classify.ts`) so every existing import keeps working unchanged.
//
// The PROFILE REGISTRY deliberately does NOT move: `class → criterion specs` is
// a large rules table that only the gate evaluates, and dragging it into a
// package the browser bundle can reach would be scope the peek never asked for.
export const WORK_CLASSES = [
    "migration",
    "gate-or-test",
    "dod-classifier",
    "agent-process",
    "mcp-tool",
    "backend-endpoint",
    "frontend-surface",
    "agent-role-or-prompt",
    "workflow-spec",
    "docs",
    // THE FLOOR (#44082). Deliberately LAST, and deliberately unreachable from
    // RULES: no path is ever classified `unclassified` by a rule. It is the class
    // a whole DIFF takes when no other class claimed ANY of its paths — see
    // criteriaForDiff(). Listing it here is what puts it under
    // assertRegistryValid()'s "every class carries ≥1 criterion" invariant, so
    // the floor cannot silently become empty.
    "unclassified",
];
const WORK_CLASS_SET = new Set(WORK_CLASSES);
/** Is this string one of the nine classes the classifier can produce? */
export function isWorkClass(value) {
    return WORK_CLASS_SET.has(value);
}
/**
 * Is this criterion BINDING on the `done` gate yet? (§6.5 / §6.6)
 *
 * A **derived** or **learned** criterion is a *proposal* until the customer
 * confirms it (detect-don't-ask): it evaluates and surfaces, but does NOT gate
 * `done` until `bound_at` is stamped. An **authored** or **customer** criterion
 * is binding immediately — someone deliberately wrote it. So a criterion gates
 * iff it is confirmed (`bound_at` set) OR its source is not one of the
 * proposal sources. This keeps the installed authored-criteria contract
 * (test-done-router) byte-identical while making derivation safely additive:
 * a freshly derived DoD never blocks a task until the customer says "yes".
 */
export function isBindingCriterion(c) {
    // An explicit drop tombstone wins over every source (#44184): a dropped
    // criterion is NEVER binding, even an authored/customer one that would
    // otherwise bind immediately.
    if (c.dropped_at != null)
        return false;
    if (c.bound_at != null)
        return true;
    return c.source !== "derived" && c.source !== "learned";
}
/**
 * The same predicate as a SQL fragment, for the aggregate queries that must not
 * pull every row into JS to count them. Kept BESIDE the function on purpose:
 * two expressions of one rule drift the moment they are in two files.
 *
 * Reads `dod_criteria` columns unqualified — prefix a table alias if the query
 * joins (`BINDING_SQL.replaceAll("bound_at", "c.bound_at")` is NOT how to do
 * that; pass the alias instead).
 */
export function bindingSql(alias = "") {
    const p = alias ? `${alias}.` : "";
    return `(${p}dropped_at IS NULL AND (${p}bound_at IS NOT NULL OR ${p}source NOT IN ('derived','learned')))`;
}
// ---------------------------------------------------------------------------
// dod:reclassified — the human labeling seam (#44086)
// ---------------------------------------------------------------------------
/**
 * A human confirmed or corrected the work-class the deriver assigned.
 *
 * Kind naming follows dod-ops.ts's family (`dod:classified`, `dod:evaluated`,
 * …) so `dod mine`'s ledger window — which selects `kind LIKE 'dod:%'` — picks
 * it up with no query change.
 */
export const DOD_RECLASSIFIED = "dod:reclassified";
/**
 * The `detail_key` a `dod:reclassified` emit MUST carry.
 *
 * LOAD-BEARING, and the degradation is silent. `emitOpsEvent` dedupes on
 * `fingerprint = sha256(kind + "\0" + detail_key)` and `detail_key` DEFAULTS TO
 * THE KIND — so emitting this without an explicit key would collapse EVERY
 * label in the fleet onto one row whose `last_seen_at` keeps moving. The
 * labeling loop would then report "1 label" forever no matter how many
 * corrections were made, and the per-class metrics it feeds would read as a
 * corpus that never grows. That is exactly mechanic 1 of dod-ops.ts's header.
 *
 * Task + registry + ASSERTED CLASSES, not task alone: a human legitimately
 * re-labels the same task after the classifier changes (new registry sha) or
 * after changing their mind (different classes), and each of those is a
 * distinct label worth its own row. Re-submitting the IDENTICAL label on the
 * same task under the same registry is genuinely the same decision and is
 * MEANT to collapse — which is what makes the confirm control idempotent
 * instead of a way to inflate the corpus by clicking twice.
 */
export function reclassifyDetailKey(input) {
    const classes = [...input.classes].sort().join("+") || "none";
    return `task:${input.taskId}:${input.registrySha}:${classes}`;
}
/** The label array written to `excellent_eval_cases.labels` for one correction. */
export function reclassifyLabels(verdict, classes) {
    return ["dod-work-class", verdict, ...classes.map((c) => `class:${c}`)];
}
